import { useState } from 'react';
import {
  useGetTeamQuery,
  useInviteMemberMutation,
  useRemoveMemberMutation,
  useUpdateMemberMutation,
  type MembershipRecord
} from '@/store/accountApi';
import { useAppDispatch, useAppSelector } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, EmptyState, Field, Panel, SkeletonRows } from '@/components/ui';
import { formatDateTime } from '@/utils/format';

/** Roles an owner can hand out, described by what the person can actually do. */
const ROLES = [
  { value: 'ORG_ADMIN', label: 'Administrator', description: 'Everything except billing ownership' },
  { value: 'LOCATION_MANAGER', label: 'Manager', description: 'Menu, hours, answers and orders' },
  { value: 'STAFF', label: 'Staff', description: 'Mark items unavailable, view calls and orders' },
  { value: 'BILLING_ADMIN', label: 'Billing', description: 'Plan, invoices and payment method' },
  { value: 'READ_ONLY', label: 'View only', description: 'Can look, cannot change anything' }
];

const roleLabel = (role: string) =>
  ROLES.find((entry) => entry.value === role)?.label ??
  (role === 'ORG_OWNER' ? 'Owner' : role);

export function TeamPage() {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector((state) => state.auth.user?.id);

  const { data, isLoading } = useGetTeamQuery();
  const [invite, inviteState] = useInviteMemberMutation();
  const [update] = useUpdateMemberMutation();
  const [remove] = useRemoveMemberMutation();

  const [form, setForm] = useState({ email: '', role: 'STAFF' });

  const send = async () => {
    if (!form.email.trim()) {
      dispatch(pushToast('Enter an email address.', 'error'));
      return;
    }

    const result = await invite({ email: form.email.trim(), role: form.role });
    if ('error' in result) {
      dispatch(pushToast('Could not send that invitation.', 'error'));
      return;
    }
    setForm({ email: '', role: 'STAFF' });
    dispatch(pushToast('Invitation sent.', 'success'));
  };

  const changeRole = async (membership: MembershipRecord, role: string) => {
    const result = await update({ id: membership._id, role });
    dispatch(
      'error' in result
        ? pushToast('Could not change that role.', 'error')
        : pushToast('Role updated.', 'success')
    );
  };

  const revoke = async (membership: MembershipRecord) => {
    const result = await remove(membership._id);
    dispatch(
      'error' in result
        ? pushToast('Could not remove that person.', 'error')
        : pushToast('Access removed.', 'success')
    );
  };

  return (
    <div className="space-y-4">
      <h1 className="text-display">Team</h1>

      <Panel title="People with access">
        {isLoading ? (
          <SkeletonRows rows={3} />
        ) : (data?.data.length ?? 0) === 0 ? (
          <EmptyState headline="Nobody else yet" hint="Invite your managers so they can keep the menu and hours current." />
        ) : (
          <ul className="divide-y divide-rule">
            {data?.data.map((membership) => {
              const isOwner = membership.role === 'ORG_OWNER';
              const isSelf = membership.userId?._id === currentUserId;

              return (
                <li key={membership._id} className="flex flex-wrap items-center gap-3 px-4 py-3.5">
                  <div className="min-w-0">
                    <p className="text-small text-ink">
                      {membership.userId
                        ? `${membership.userId.firstName} ${membership.userId.lastName}`
                        : 'Pending invitation'}
                      {isSelf ? <span className="text-slate"> (you)</span> : null}
                    </p>
                    <p className="text-micro text-slate">{membership.userId?.email}</p>
                  </div>

                  {membership.status === 'INVITED' ? (
                    <span className="rounded-sm bg-amber-soft px-2 py-0.5 text-micro text-amber">
                      Invitation sent
                    </span>
                  ) : null}

                  <span className="tabular ml-auto text-micro text-slate">
                    {membership.userId?.lastLoginAt
                      ? `Last signed in ${formatDateTime(membership.userId.lastLoginAt)}`
                      : 'Never signed in'}
                  </span>

                  {isOwner ? (
                    <span className="text-small text-slate">{roleLabel(membership.role)}</span>
                  ) : (
                    <select
                      value={membership.role}
                      onChange={(event) => changeRole(membership, event.target.value)}
                      className="rounded border border-rule bg-white px-2 py-1 text-small"
                      aria-label={`Role for ${membership.userId?.email ?? 'this person'}`}
                    >
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {isOwner || isSelf ? null : (
                    <button
                      onClick={() => revoke(membership)}
                      className="text-small text-slate hover:text-rust"
                    >
                      Remove
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel title="Invite someone">
        <div className="max-w-md space-y-4 px-4 py-4">
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />

          <div className="space-y-1.5">
            <label htmlFor="role" className="block text-small font-medium text-ink">
              What they can do
            </label>
            <select
              id="role"
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value })}
              className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
            >
              {ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            <p className="text-micro text-slate">
              {ROLES.find((role) => role.value === form.role)?.description}
            </p>
          </div>

          <Button loading={inviteState.isLoading} onClick={send}>
            Send invitation
          </Button>
        </div>
      </Panel>
    </div>
  );
}
